import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import {
  COMPLAINTS_CONFIG,
  COMPLAINTS_PROVIDER,
  DEFAULT_RECLAMATION_VALUES,
  DOCUMENT_TYPES,
  RECEIPT_TYPES,
  type ReclamationFormValues,
} from '../../data/reclamaciones';
import {
  buildReclamationPayload,
  getPeruDateInputValue,
  submitReclamation,
  validateReclamation,
  type FormErrors,
  type SubmissionResult,
} from '../../lib/reclamaciones';
import {
  DEPARTMENTS,
  loadDistricts,
  loadProvinces,
  type DistrictOption,
  type ProvinceOption,
} from '../../lib/ubigeo';
import { ReclamationReview } from './ReclamationReview';

const fieldErrorId = (name: keyof ReclamationFormValues) => `reclamation-${name}-error`;

function FieldError({ name, message }: { name: keyof ReclamationFormValues; message?: string }) {
  return message ? (
    <span id={fieldErrorId(name)} className="complaints-field-error" role="alert">
      {message}
    </span>
  ) : null;
}

function Section({ number, title, description, children }: {
  number: number;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="complaints-card">
      <header className="complaints-card__header">
        <span>{number}</span>
        <div><h2>{title}</h2><p>{description}</p></div>
      </header>
      <div className="complaints-card__body">{children}</div>
    </section>
  );
}

const Required = () => <span className="complaints-required" aria-hidden="true"> *</span>;

export function ReclamationForm() {
  const [values, setValues] = useState<ReclamationFormValues>(DEFAULT_RECLAMATION_VALUES);
  const [errors, setErrors] = useState<FormErrors>({});
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<SubmissionResult | null>(null);
  const [provinces, setProvinces] = useState<ProvinceOption[]>([]);
  const [districts, setDistricts] = useState<DistrictOption[]>([]);
  const [provincesLoading, setProvincesLoading] = useState(false);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [ubigeoError, setUbigeoError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const requestIdRef = useRef(crypto.randomUUID());

  const today = useMemo(() => getPeruDateInputValue(), []);
  const presentationDate = useMemo(() => new Intl.DateTimeFormat('es-PE', {
    dateStyle: 'long',
    timeZone: 'America/Lima',
  }).format(new Date()), []);

  useEffect(() => {
    if (!values.departamentoId) {
      setProvinces([]);
      setProvincesLoading(false);
      setUbigeoError('');
      return;
    }
    const controller = new AbortController();
    let active = true;
    setProvincesLoading(true);
    setUbigeoError('');
    loadProvinces(values.departamentoId, controller.signal)
      .then((items) => {
        if (active) setProvinces(items);
      })
      .catch((error: unknown) => {
        if (active && (error as { name?: string }).name !== 'AbortError') {
          setUbigeoError('No se pudieron cargar las provincias. Intenta cambiar el departamento y volver a seleccionarlo.');
        }
      })
      .finally(() => {
        if (active) setProvincesLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [values.departamentoId]);

  useEffect(() => {
    if (!values.provinciaId) {
      setDistricts([]);
      setDistrictsLoading(false);
      return;
    }
    const controller = new AbortController();
    let active = true;
    setDistrictsLoading(true);
    setUbigeoError('');
    loadDistricts(values.provinciaId, controller.signal)
      .then((items) => {
        if (active) setDistricts(items);
      })
      .catch((error: unknown) => {
        if (active && (error as { name?: string }).name !== 'AbortError') {
          setUbigeoError('No se pudieron cargar los distritos. Intenta seleccionar nuevamente la provincia.');
        }
      })
      .finally(() => {
        if (active) setDistrictsLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [values.provinciaId]);

  const clearErrors = (...keys: (keyof ReclamationFormValues)[]) => {
    setErrors((current) => {
      if (!keys.some((key) => current[key])) return current;
      const next = { ...current };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  };

  const fieldState = (name: keyof ReclamationFormValues) => ({
    'aria-invalid': errors[name] ? true : undefined,
    'aria-describedby': errors[name] ? fieldErrorId(name) : undefined,
  });

  const update = <K extends keyof ReclamationFormValues>(key: K, value: ReclamationFormValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const chooseDepartment = (event: ChangeEvent<HTMLSelectElement>) => {
    const id = event.target.value;
    const option = DEPARTMENTS.find((item) => item.id === id);
    setValues((current) => ({
      ...current,
      departamentoId: id,
      departamento: option?.name ?? '',
      provinciaId: '',
      provincia: '',
      distritoId: '',
      distrito: '',
      ubigeo: '',
    }));
    setProvinces([]);
    setDistricts([]);
    clearErrors('departamentoId', 'provinciaId', 'distritoId');
  };

  const chooseProvince = (event: ChangeEvent<HTMLSelectElement>) => {
    const id = event.target.value;
    const option = provinces.find((item) => item.id === id);
    setValues((current) => ({
      ...current,
      provinciaId: id,
      provincia: option?.name ?? '',
      distritoId: '',
      distrito: '',
      ubigeo: '',
    }));
    setDistricts([]);
    clearErrors('provinciaId', 'distritoId');
  };

  const chooseDistrict = (event: ChangeEvent<HTMLSelectElement>) => {
    const id = event.target.value;
    const option = districts.find((item) => item.id === id);
    setValues((current) => ({
      ...current,
      distritoId: id,
      distrito: option?.name ?? '',
      ubigeo: option?.inei ?? '',
    }));
    clearErrors('distritoId');
  };

  const chooseDocumentType = (event: ChangeEvent<HTMLSelectElement>) => {
    update('tipoDocumento', event.target.value as ReclamationFormValues['tipoDocumento']);
    clearErrors('numeroDocumento');
  };

  const chooseRepresentativeDocumentType = (event: ChangeEvent<HTMLSelectElement>) => {
    update('tipoDocumentoRepresentante', event.target.value as ReclamationFormValues['tipoDocumentoRepresentante']);
    clearErrors('numeroDocumentoRepresentante');
  };

  const toggleMinor = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    update('esMenorEdad', checked);
    if (!checked) {
      clearErrors(
        'nombresRepresentante',
        'numeroDocumentoRepresentante',
        'telefonoRepresentante',
        'correoRepresentante',
        'direccionRepresentante',
      );
    }
  };

  const chooseReceiptType = (event: ChangeEvent<HTMLSelectElement>) => {
    const type = event.target.value as ReclamationFormValues['tipoComprobante'];
    setValues((current) => ({
      ...current,
      tipoComprobante: type,
      numeroComprobante: type === 'Sin comprobante' ? '' : current.numeroComprobante,
    }));
    clearErrors('numeroComprobante');
  };

  const addFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    setFileError('');
    if (!selected.length) return;

    const next = [...files];
    for (const file of selected) {
      if (next.length >= COMPLAINTS_CONFIG.maxFiles) {
        setFileError(`Puedes adjuntar como máximo ${COMPLAINTS_CONFIG.maxFiles} archivos.`);
        break;
      }
      if (!COMPLAINTS_CONFIG.allowedMimeTypes.includes(file.type as never)) {
        setFileError('Solo se permiten archivos PDF, JPG, PNG o WEBP.');
        continue;
      }
      if (file.size > COMPLAINTS_CONFIG.maxFileBytes) {
        setFileError('Cada archivo debe pesar como máximo 10 MB.');
        continue;
      }
      if (next.some((item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified)) continue;
      next.push(file);
    }
    const total = next.reduce((sum, file) => sum + file.size, 0);
    if (total > COMPLAINTS_CONFIG.maxTotalFileBytes) {
      setFileError('El peso total de los archivos no puede superar 15 MB.');
      return;
    }
    setFiles(next);
  };

  const removeFile = (file: File) => {
    setFiles((current) => current.filter((item) => !(item.name === file.name && item.lastModified === file.lastModified)));
    setFileError('');
  };

  const goToReview = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validation = validateReclamation(values);
    setErrors(validation);
    if (Object.keys(validation).length) {
      const first = Object.keys(validation)[0];
      document.querySelector<HTMLElement>(`[name="${first}"]`)?.focus();
      return;
    }
    setSubmitError('');
    setReviewing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirm = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = buildReclamationPayload(values, files);
      const submission = await submitReclamation(payload, files, requestIdRef.current, values.website);
      setResult(submission);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'No fue posible registrar la hoja.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setValues(DEFAULT_RECLAMATION_VALUES);
    setErrors({});
    setFiles([]);
    setReviewing(false);
    setSubmitError('');
    setResult(null);
    setProvinces([]);
    setDistricts([]);
    requestIdRef.current = crypto.randomUUID();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    requestAnimationFrame(() => formRef.current?.focus());
  };

  const backToForm = () => {
    setReviewing(false);
    setSubmitError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    requestAnimationFrame(() => formRef.current?.focus());
  };

  if (reviewing) {
    return (
      <ReclamationReview
        values={values}
        files={files}
        submitting={submitting}
        submitError={submitError}
        result={result}
        onBack={backToForm}
        onConfirm={confirm}
        onNew={reset}
      />
    );
  }

  return (
    <form ref={formRef} className="complaints-form" onSubmit={goToReview} noValidate tabIndex={-1} aria-label="Formulario del Libro de Reclamaciones">
      <p className="complaints-alert complaints-alert--info">
        Los campos marcados con * son obligatorios. Al finalizar podrás revisar toda la información antes del registro definitivo.
      </p>

      <Section number={1} title="Identificación del proveedor" description="Información legal fija de la empresa responsable del Libro de Reclamaciones.">
        <dl className="complaints-provider-grid">
          <div><dt>Razón social</dt><dd>{COMPLAINTS_PROVIDER.razonSocial}</dd></div>
          <div><dt>RUC</dt><dd>{COMPLAINTS_PROVIDER.ruc}</dd></div>
          <div><dt>Nombre comercial</dt><dd>{COMPLAINTS_PROVIDER.nombreComercial}</dd></div>
          <div><dt>Fecha de presentación</dt><dd>{presentationDate}</dd></div>
          <div className="complaints-provider-grid__wide"><dt>Dirección</dt><dd>{COMPLAINTS_PROVIDER.direccion}</dd></div>
          <div className="complaints-provider-grid__wide"><dt>Número de hoja</dt><dd>Será generado por el servidor al registrar la solicitud.</dd></div>
        </dl>
      </Section>

      <Section number={2} title="Identificación del consumidor" description="Ingresa datos reales y medios de contacto vigentes.">
        <div className="complaints-grid complaints-grid--2">
          <label className="complaints-field"><span>Nombres<Required /></span><input name="nombres" value={values.nombres} onChange={(e) => update('nombres', e.target.value)} autoComplete="given-name" required {...fieldState('nombres')} /><FieldError name="nombres" message={errors.nombres} /></label>
          <label className="complaints-field"><span>Apellidos<Required /></span><input name="apellidos" value={values.apellidos} onChange={(e) => update('apellidos', e.target.value)} autoComplete="family-name" required {...fieldState('apellidos')} /><FieldError name="apellidos" message={errors.apellidos} /></label>
          <label className="complaints-field"><span>Tipo de documento<Required /></span><select name="tipoDocumento" value={values.tipoDocumento} onChange={chooseDocumentType} required>{DOCUMENT_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="complaints-field"><span>Número de documento<Required /></span><input name="numeroDocumento" value={values.numeroDocumento} onChange={(e) => update('numeroDocumento', e.target.value)} inputMode="numeric" required {...fieldState('numeroDocumento')} /><FieldError name="numeroDocumento" message={errors.numeroDocumento} /></label>
          <label className="complaints-field"><span>Teléfono<Required /></span><input name="telefono" type="tel" value={values.telefono} onChange={(e) => update('telefono', e.target.value)} autoComplete="tel" required {...fieldState('telefono')} /><FieldError name="telefono" message={errors.telefono} /></label>
          <label className="complaints-field"><span>Correo electrónico<Required /></span><input name="correo" type="email" value={values.correo} onChange={(e) => update('correo', e.target.value)} autoComplete="email" required {...fieldState('correo')} /><FieldError name="correo" message={errors.correo} /></label>
          <label className="complaints-field complaints-field--wide"><span>Domicilio<Required /></span><input name="direccion" value={values.direccion} onChange={(e) => update('direccion', e.target.value)} autoComplete="street-address" required {...fieldState('direccion')} /><FieldError name="direccion" message={errors.direccion} /></label>
          <label className="complaints-field"><span>Departamento<Required /></span><select name="departamentoId" value={values.departamentoId} onChange={chooseDepartment} required {...fieldState('departamentoId')}><option value="">Selecciona</option>{DEPARTMENTS.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><FieldError name="departamentoId" message={errors.departamentoId} /></label>
          <label className="complaints-field"><span>Provincia<Required /></span><select name="provinciaId" value={values.provinciaId} onChange={chooseProvince} disabled={!values.departamentoId || provincesLoading} required aria-busy={provincesLoading || undefined} {...fieldState('provinciaId')}><option value="">{provincesLoading ? 'Cargando provincias…' : values.departamentoId ? 'Selecciona' : 'Selecciona primero un departamento'}</option>{provinces.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><FieldError name="provinciaId" message={errors.provinciaId} /></label>
          <label className="complaints-field"><span>Distrito<Required /></span><select name="distritoId" value={values.distritoId} onChange={chooseDistrict} disabled={!values.provinciaId || districtsLoading} required aria-busy={districtsLoading || undefined} {...fieldState('distritoId')}><option value="">{districtsLoading ? 'Cargando distritos…' : values.provinciaId ? 'Selecciona' : 'Selecciona primero una provincia'}</option>{districts.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><FieldError name="distritoId" message={errors.distritoId} /></label>
          <div className="complaints-field"><span>UBIGEO INEI</span><output>{values.ubigeo || 'Se completa al elegir el distrito'}</output></div>
        </div>
        {provincesLoading || districtsLoading ? <p className="sr-only" role="status" aria-live="polite">Cargando opciones de ubicación.</p> : null}
        {ubigeoError ? <p className="complaints-alert complaints-alert--error" role="alert">{ubigeoError}</p> : null}

        <label className="complaints-check"><input name="esMenorEdad" type="checkbox" checked={values.esMenorEdad} onChange={toggleMinor} /><span>El consumidor es menor de edad</span></label>

        {values.esMenorEdad ? (
          <fieldset className="complaints-subsection">
            <legend>Datos del padre, madre o representante</legend>
            <div className="complaints-grid complaints-grid--2">
              <label className="complaints-field complaints-field--wide"><span>Nombres y apellidos<Required /></span><input name="nombresRepresentante" value={values.nombresRepresentante} onChange={(e) => update('nombresRepresentante', e.target.value)} required {...fieldState('nombresRepresentante')} /><FieldError name="nombresRepresentante" message={errors.nombresRepresentante} /></label>
              <label className="complaints-field"><span>Tipo de documento<Required /></span><select name="tipoDocumentoRepresentante" value={values.tipoDocumentoRepresentante} onChange={chooseRepresentativeDocumentType} required>{DOCUMENT_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label className="complaints-field"><span>Número de documento<Required /></span><input name="numeroDocumentoRepresentante" value={values.numeroDocumentoRepresentante} onChange={(e) => update('numeroDocumentoRepresentante', e.target.value)} required {...fieldState('numeroDocumentoRepresentante')} /><FieldError name="numeroDocumentoRepresentante" message={errors.numeroDocumentoRepresentante} /></label>
              <label className="complaints-field"><span>Teléfono<Required /></span><input name="telefonoRepresentante" type="tel" value={values.telefonoRepresentante} onChange={(e) => update('telefonoRepresentante', e.target.value)} required {...fieldState('telefonoRepresentante')} /><FieldError name="telefonoRepresentante" message={errors.telefonoRepresentante} /></label>
              <label className="complaints-field"><span>Correo<Required /></span><input name="correoRepresentante" type="email" value={values.correoRepresentante} onChange={(e) => update('correoRepresentante', e.target.value)} required {...fieldState('correoRepresentante')} /><FieldError name="correoRepresentante" message={errors.correoRepresentante} /></label>
              <label className="complaints-field complaints-field--wide"><span>Domicilio<Required /></span><input name="direccionRepresentante" value={values.direccionRepresentante} onChange={(e) => update('direccionRepresentante', e.target.value)} required {...fieldState('direccionRepresentante')} /><FieldError name="direccionRepresentante" message={errors.direccionRepresentante} /></label>
            </div>
          </fieldset>
        ) : null}
      </Section>

      <Section number={3} title="Producto o servicio" description="Identifica la relación de consumo vinculada a la solicitud.">
        <div className="complaints-choice-grid">
          {(['Producto', 'Servicio'] as const).map((item) => <label className={`complaints-choice${values.tipoBien === item ? ' is-selected' : ''}`} key={item}><input type="radio" name="tipoBien" checked={values.tipoBien === item} onChange={() => update('tipoBien', item)} /><strong>{item}</strong></label>)}
        </div>
        <div className="complaints-grid complaints-grid--2">
          <label className="complaints-field complaints-field--wide"><span>Descripción del producto o servicio<Required /></span><textarea name="descripcionBienServicio" rows={4} value={values.descripcionBienServicio} onChange={(e) => update('descripcionBienServicio', e.target.value)} maxLength={500} required {...fieldState('descripcionBienServicio')} /><small>{values.descripcionBienServicio.length}/500</small><FieldError name="descripcionBienServicio" message={errors.descripcionBienServicio} /></label>
          <label className="complaints-field"><span>Fecha de adquisición o contratación</span><input name="fechaAdquisicion" type="date" max={today} value={values.fechaAdquisicion} onChange={(e) => update('fechaAdquisicion', e.target.value)} {...fieldState('fechaAdquisicion')} /><FieldError name="fechaAdquisicion" message={errors.fechaAdquisicion} /></label>
          <label className="complaints-field"><span>Monto reclamado</span><div className="complaints-inline"><select aria-label="Moneda" value={values.moneda} onChange={(e) => update('moneda', e.target.value as ReclamationFormValues['moneda'])}><option value="PEN">S/</option><option value="USD">US$</option></select><input name="montoReclamado" inputMode="decimal" value={values.montoReclamado} onChange={(e) => update('montoReclamado', e.target.value)} placeholder="0.00" {...fieldState('montoReclamado')} /></div><FieldError name="montoReclamado" message={errors.montoReclamado} /></label>
          <label className="complaints-field"><span>Tipo de comprobante<Required /></span><select name="tipoComprobante" value={values.tipoComprobante} onChange={chooseReceiptType} required>{RECEIPT_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="complaints-field"><span>Número o referencia{values.tipoComprobante !== 'Sin comprobante' ? <Required /> : null}</span><input name="numeroComprobante" value={values.numeroComprobante} onChange={(e) => update('numeroComprobante', e.target.value)} disabled={values.tipoComprobante === 'Sin comprobante'} required={values.tipoComprobante !== 'Sin comprobante'} {...fieldState('numeroComprobante')} /><FieldError name="numeroComprobante" message={errors.numeroComprobante} /></label>
        </div>
      </Section>

      <Section number={4} title="Detalle del reclamo o queja" description="Selecciona la categoría adecuada y describe con claridad lo ocurrido.">
        <div className="complaints-choice-grid">
          <label className={`complaints-choice${values.tipoRegistro === 'Reclamo' ? ' is-selected' : ''}`}><input type="radio" name="tipoRegistro" checked={values.tipoRegistro === 'Reclamo'} onChange={() => update('tipoRegistro', 'Reclamo')} /><strong>Reclamo</strong><span>Disconformidad relacionada con un producto o servicio.</span></label>
          <label className={`complaints-choice${values.tipoRegistro === 'Queja' ? ' is-selected' : ''}`}><input type="radio" name="tipoRegistro" checked={values.tipoRegistro === 'Queja'} onChange={() => update('tipoRegistro', 'Queja')} /><strong>Queja</strong><span>Malestar o descontento respecto de la atención recibida.</span></label>
        </div>
        <div className="complaints-grid">
          <label className="complaints-field"><span>Detalle de lo ocurrido<Required /></span><textarea name="detalle" rows={7} maxLength={4000} value={values.detalle} onChange={(e) => update('detalle', e.target.value)} required {...fieldState('detalle')} /><small>{values.detalle.length}/4000</small><FieldError name="detalle" message={errors.detalle} /></label>
          <label className="complaints-field"><span>Pedido concreto del consumidor<Required /></span><textarea name="pedido" rows={5} maxLength={1500} value={values.pedido} onChange={(e) => update('pedido', e.target.value)} required {...fieldState('pedido')} /><small>{values.pedido.length}/1500</small><FieldError name="pedido" message={errors.pedido} /></label>
          <label className="complaints-field"><span>Medio para recibir la respuesta<Required /></span><select name="medioRespuesta" value={values.medioRespuesta} onChange={(e) => update('medioRespuesta', e.target.value as ReclamationFormValues['medioRespuesta'])} required><option>Correo electrónico</option><option>Carta al domicilio</option></select></label>
        </div>
      </Section>

      <Section number={5} title="Documentos adjuntos" description="Puedes adjuntar comprobantes, contratos, imágenes u otros sustentos.">
        <div className="complaints-upload">
          <input ref={fileInputRef} className="sr-only" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple onChange={addFiles} />
          <button type="button" className="complaints-button complaints-button--secondary" onClick={() => fileInputRef.current?.click()}>Seleccionar archivos</button>
          <p>PDF, JPG, PNG o WEBP. Máximo 5 archivos, 10 MB por archivo y 15 MB en total.</p>
        </div>
        {files.length ? <ul className="complaints-file-list">{files.map((file) => <li key={`${file.name}-${file.lastModified}`}><span>{file.name}</span><button type="button" onClick={() => removeFile(file)} aria-label={`Quitar ${file.name}`}>Quitar</button></li>)}</ul> : null}
        {fileError ? <p className="complaints-field-error" role="alert">{fileError}</p> : null}
      </Section>

      <Section number={6} title="Declaraciones" description="Confirma la veracidad de los datos y el tratamiento necesario para atender tu solicitud.">
        <label className="complaints-check"><input name="declaracionVeracidad" type="checkbox" checked={values.declaracionVeracidad} onChange={(e) => update('declaracionVeracidad', e.target.checked)} required {...fieldState('declaracionVeracidad')} /><span>Declaro que la información proporcionada es verdadera.<Required /></span></label><FieldError name="declaracionVeracidad" message={errors.declaracionVeracidad} />
        <label className="complaints-check"><input name="aceptaPrivacidad" type="checkbox" checked={values.aceptaPrivacidad} onChange={(e) => update('aceptaPrivacidad', e.target.checked)} required {...fieldState('aceptaPrivacidad')} /><span>Confirmo que he leído el <a href="/politica-de-privacidad" target="_blank" rel="noopener noreferrer">aviso de privacidad</a> y autorizo el tratamiento necesario para atender el reclamo o queja.<Required /></span></label><FieldError name="aceptaPrivacidad" message={errors.aceptaPrivacidad} />
        <input className="complaints-honeypot" name="website" value={values.website} onChange={(e) => update('website', e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <p className="complaints-legal-note">La presentación de esta hoja no impide acudir a otras vías de solución de controversias. El proveedor debe responder por escrito dentro del plazo legal aplicable.</p>
      </Section>

      <div className="complaints-submit-wrap"><button className="complaints-button complaints-button--large" type="submit">Revisar antes de enviar</button></div>
    </form>
  );
}
